import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  FormHelperText,
  TextField,
  MenuItem,
  useTheme,
} from "@mui/material";
import {
  PRIMARY_PINK,
  PRIMARY_PINK_DARK,
  TRANSPARENT_BLACK_08,
} from "../constants";

const CAMPAIGN_OPTIONS = [
  "One-Shot (1 session)",
  "Mini (3 sessions)",
  "Mid (4-6 sessions)",
  "Long (7+ sessions)",
];

const CLASS_OPTIONS = [
  "Artificer",
  "Barbarian",
  "Bard",
  "Cleric",
  "Druid",
  "Fighter",
  "Monk",
  "Paladin",
  "Ranger",
  "Sorcerer",
  "Warlock",
  "Wizard",
];

const EXPERIENCE_OPTIONS = ["Beginner", "Intermediate", "Advanced"];

function DndRegistrationModal({ open, onClose, onSubmit, loading }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const menuItemSx = isDark
    ? {
        color: "#ffffff !important",
        "&:hover, &:focus": {
          backgroundColor: "rgba(255, 255, 255, 0.08) !important",
        },
        "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": {
          backgroundColor: "rgba(233, 30, 99, 0.28) !important",
          color: "#ffffff !important",
        },
      }
    : {};
  const [campaigns, setCampaigns] = useState([]);
  const [favoriteClasses, setFavoriteClasses] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState("");
  const [error, setError] = useState("");

  const handleToggle = (setList, value) => {
    setError("");
    setList((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (campaigns.length === 0) {
      setError("Please select at least one campaign type");
      return;
    }

    if (favoriteClasses.length === 0) {
      setError("Please select at least one favorite class");
      return;
    }

    if (!experienceLevel) {
      setError("Please select your experience level");
      return;
    }

    onSubmit({
      campaigns: CAMPAIGN_OPTIONS.filter((option) => campaigns.includes(option)).join(", "),
      favorite_classes: CLASS_OPTIONS.filter((option) => favoriteClasses.includes(option)).join(", "),
      experience_level: experienceLevel,
    });
  };

  const handleClose = () => {
    setCampaigns([]);
    setFavoriteClasses([]);
    setExperienceLevel("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          DnD Registration Form
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please tell us about your experience, campaigns, and classes
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="dense"
            required
            fullWidth
            select
            id="experience_level"
            label="Experience Level"
            name="experience_level"
            value={experienceLevel}
            onChange={(e) => {
              setExperienceLevel(e.target.value);
              setError("");
            }}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                height: 56,
                minHeight: 56,
              },
            }}
            SelectProps={{
              sx: {
                height: 56,
                minHeight: 56,
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                  height: "56px !important",
                  minHeight: "56px !important",
                  padding: "16.5px 32px 16.5px 14px !important",
                  color: isDark ? "#ffffff" : "#000000",
                },
                "& .MuiSelect-icon": {
                  display: "block !important",
                  color: isDark ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)",
                },
              },
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: isDark ? "#1e1e1e !important" : "#ffffff !important",
                    backgroundImage: "none",
                    color: isDark ? "#ffffff" : "#000000",
                  },
                },
              },
            }}
          >
            {EXPERIENCE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option} sx={menuItemSx}>
                {option}
              </MenuItem>
            ))}
          </TextField>

          <FormControl component="fieldset" required sx={{ mb: 3, width: "100%" }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
              What kind of campaigns are you looking to play?
            </FormLabel>
            <FormGroup>
              {CAMPAIGN_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={campaigns.includes(option)}
                      onChange={() => handleToggle(setCampaigns, option)}
                      sx={{
                        color: PRIMARY_PINK,
                        "&.Mui-checked": { color: PRIMARY_PINK },
                      }}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
            <FormHelperText>Select all that apply</FormHelperText>
          </FormControl>

          <FormControl component="fieldset" required sx={{ mb: 1, width: "100%" }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
              What is your favorite class?
            </FormLabel>
            <FormGroup>
              {CLASS_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={favoriteClasses.includes(option)}
                      onChange={() => handleToggle(setFavoriteClasses, option)}
                      sx={{
                        color: PRIMARY_PINK,
                        "&.Mui-checked": { color: PRIMARY_PINK },
                      }}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
            <FormHelperText>Select all that apply</FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            color: "#000000",
            borderColor: "#000000",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              borderColor: "#000000",
              backgroundColor: TRANSPARENT_BLACK_08,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: PRIMARY_PINK,
            color: "#FFFFFF",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              bgcolor: PRIMARY_PINK_DARK,
            },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DndRegistrationModal;
